import { AppContext } from 'contexts/app-context';
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { JobsList } from 'components/jobs-list';
import { Skeleton } from 'components/ui/skeleton';
import { useJobs } from 'hooks/useJobs';

import { Pagination, PaginationSkeleton } from './pagination';
import { TrackingPixels } from './tracking-pixels';

export function JobsSectionSkeleton() {
  const { isMobile } = useContext(AppContext);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between">
        <Skeleton className="h-8 w-full md:w-1/3" />
        {!isMobile && <Skeleton className="h-8 w-[325px]" />}
      </div>
      <JobsList.SkeletonGroup />
      <PaginationSkeleton />
    </div>
  );
}

export function JobsSection() {
  const intl = useIntl();
  const {
    location,
    brandName,
    isFetchingJobs,
    jobSearchQuery,
    experienceLevelName,
    page,
    setPage,
    isMobile,
    position,
    activeFilters,
    placeId,
  } = useContext(AppContext);

  const {
    jobs,
    paginationData,
    isCurrentLocation,
    currentCity,
    trackingPixels,
  } = useJobs();
  const categoryName = activeFilters.category?.label;
  const areJobsAvailable = jobs.length > 0;
  const positionName = position.value;
  const isCmsFiltersDirty =
    activeFilters.location.key !== 'current_location' ||
    activeFilters.position.key !== 'all' ||
    activeFilters.department.key !== 'all';

  const { title, subTitle } = useMemo(() => {
    const selectedLocation = `(${currentCity ?? location.value ?? ''})`;

    if (activeFilters.location.key === 'all')
      return {
        title: intl.formatMessage({
          defaultMessage: 'All Available Openings',
          description: 'Title for all available job openings',
        }),
        subTitle: intl.formatMessage(
          {
            defaultMessage:
              'See all available openings from {brandName} below or search openings in a different location.',
            description:
              'Subtitle explaining how to view all openings or search in different location',
          },
          { brandName },
        ),
      };

    if (
      (activeFilters.location.key !== 'all' &&
        activeFilters.location.key !== 'current_location' &&
        areJobsAvailable) ||
      (placeId && areJobsAvailable)
    )
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Openings Near ({location})',
            description: 'Title for job openings near a specific location',
          },
          {
            location: placeId
              ? currentCity
              : activeFilters.location.label || currentCity,
          },
        ),
        subTitle: null,
      };

    if (areJobsAvailable && isCmsFiltersDirty)
      return {
        title: intl.formatMessage({
          defaultMessage: 'Available Openings',
          description: 'Title for available job openings',
        }),
        subTitle: null,
      };

    if (
      (!areJobsAvailable && isCmsFiltersDirty) ||
      (placeId && !areJobsAvailable)
    )
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'No Openings found near ({location})',
            description:
              'Title when no job openings are found near a specific location',
          },
          {
            location: placeId
              ? currentCity
              : activeFilters.location.label || currentCity,
          },
        ),
        subTitle: null,
      };

    if (jobSearchQuery)
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Search results for "{query}"',
            description: 'Title for search results with the search query',
          },
          { query: jobSearchQuery },
        ),
        subTitle: null,
      };

    if (
      activeFilters.category?.key !== 'any' &&
      categoryName &&
      areJobsAvailable
    )
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Openings in {category}',
            description: 'Title for job openings in a specific category',
          },
          { category: categoryName },
        ),
        subTitle: null,
      };

    if (experienceLevelName && areJobsAvailable)
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Openings in {experienceLevel}',
            description:
              'Title for job openings with a specific experience level',
          },
          { experienceLevel: experienceLevelName },
        ),
        subTitle: null,
      };

    if (positionName && areJobsAvailable)
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Openings in {position}',
            description: 'Title for job openings in a specific position',
          },
          { position: positionName },
        ),
        subTitle: null,
      };

    if (
      areJobsAvailable &&
      isCurrentLocation &&
      (currentCity || location.value)
    )
      return {
        title: intl.formatMessage(
          {
            defaultMessage: 'Openings Near You {location}',
            description:
              "Title for job openings near the user's current location",
          },
          { location: selectedLocation },
        ),
        subTitle: null,
      };

    return {
      title: intl.formatMessage(
        {
          defaultMessage: 'No Nearby Openings found {location}',
          description: 'Title when no nearby job openings are found',
        },
        { location: selectedLocation },
      ),
      subTitle: intl.formatMessage(
        {
          defaultMessage:
            'See all available openings from {brandName} below or search openings in a different location.',
          description:
            'Subtitle explaining how to view all openings or search in different location when none are found nearby',
        },
        { brandName },
      ),
    };
  }, [
    currentCity,
    location.value,
    jobSearchQuery,
    categoryName,
    areJobsAvailable,
    experienceLevelName,
    positionName,
    isCurrentLocation,
    brandName,
    isCmsFiltersDirty,
    activeFilters,
    placeId,
    intl,
  ]);

  if (
    (!isMobile && isFetchingJobs) ||
    (isMobile && isFetchingJobs && page === 1)
  ) {
    return <JobsSectionSkeleton />;
  }

  return (
    <div className="flex flex-col gap-3">
      <TrackingPixels pixels={trackingPixels} />
      <div className="flex flex-row items-center justify-between">
        <h2 className="text-24 font-500 leading-8">{title}</h2>
      </div>
      {subTitle && <p className="text-18 font-400 leading-6">{subTitle}</p>}

      <JobsList jobs={jobs} />

      <Pagination
        currentPage={page}
        lastPage={paginationData?.total_pages ?? 1}
        setPage={setPage}
        jobsCount={paginationData?.total_count ?? 0}
      />
    </div>
  );
}
